/**
 * Module.js - Composant de gestion des modules de fibres optiques
 *
 * VERSION REFACTORISÉE - 100% basée sur data[]
 * =============================================
 *
 * Suppression complète de l'array local this.statuses
 * Utilise directement project.data[] comme source de vérité
 * Intégration complète avec le système de reconnaissance vocale
 *
 * @author ITE Technologies
 * @version 2.0.0
 * @since 2025-01-17
 */
import "./Module.css";
import { SpeechRecognition } from "../../utils/SpeechRecognition.js";
import { createElement } from "../../utils/dom.js";
import {
  STATUS_ORDER,
  STATUS_COLORS,
  STATUS_IMAGES,
  parseModuleName,
  normalizeStatus,
  CUSTOM_EVENTS,
} from "../../utils/constants.js";
import {
  loadCurrentProject,
  saveProject,
  getModuleStatuses,
  setFiberStatus,
  setModuleStatuses,
  getModuleStats,
} from "../../utils/storage.js";

export class Module {
  /**
   * Constructeur du composant Module
   *
   * @param {string} title - Titre du module (ex: "TIROIR : T1 - MODULE A")
   * @param {HTMLElement} container - Container DOM pour le rendu
   * @param {Array<string>|null} initialStatuses - Statuts initiaux (DEPRECATED - ignoré)
   */
  constructor(title, container, initialStatuses = null) {
    // Propriétés de base
    this.title = title;
    this.container = container;

    // Parser le titre pour extraire tiroir et module
    const parsed = parseModuleName(title);
    if (!parsed) {
      throw new Error(`Format de titre invalide: ${title}`);
    }

    this.tiroir = parsed.tiroir; // Ex: "T1"
    this.module = parsed.module; // Ex: "A"

    // État du composant
    this.vocalMode = false;
    this.currentFiber = 1;
    this.speechStatus = "En attente...";
    this.lastCommand = "";
    this.lastHeard = "";
    this.speechRecognition = null;

    // Propriétés de tracking
    this.lastModifiedFiber = null;
    this.sessionId = this.generateSessionId();

    // Charger le projet et la configuration
    this.project = loadCurrentProject();
    if (!this.project) {
      throw new Error("Aucun projet chargé");
    }

    // Charger l'état du module depuis le projet
    this.loadModuleState();

    // Initialiser le composant
    this.render();
  }

  // ===== GESTION DES DONNÉES =====

  /**
   * Charger l'état du module depuis le projet
   * Charge les données depuis project.data[] au lieu de localStorage séparé
   */
  loadModuleState() {
    // Les statuses sont maintenant toujours chargés depuis project.data[]
    // Plus de stockage séparé par module

    // Position par défaut au premier port non configuré
    this.currentFiber = this.findNextUnconfigueredFiber() || 1;
  }

  /**
   * Sauvegarder l'état du module dans le projet
   * Sauvegarde uniquement l'état global du projet, pas de stockage séparé
   */
  saveModuleState() {
    try {
      // Mettre à jour les métadonnées du projet
      this.project.lastModified = new Date().toISOString();

      // Sauvegarder le projet complet
      const success = saveProject(this.project);

      if (success) {
        // Déclencher un événement de changement de données
        this.notifyDataChanged();
      }

      return success;
    } catch (error) {
      console.error("❌ Erreur sauvegarde module:", error);
      return false;
    }
  }

  /**
   * Obtenir tous les statuts du module actuel
   *
   * @returns {Array<string>} Tableau des statuts [statut1, statut2, ...]
   */
  getStatuses() {
    return getModuleStatuses(this.project.data, this.tiroir, this.module, this.project.fiberType);
  }

  /**
   * Obtenir le statut d'une fibre spécifique
   *
   * @param {number} fiberIndex - Index de la fibre (0-23)
   * @returns {string} Statut de la fibre
   */
  getFiberStatus(fiberIndex) {
    const statuses = this.getStatuses();
    return statuses[fiberIndex] || "N/A";
  }

  /**
   * Calculer les statistiques du module
   *
   * @returns {Object} Statistiques complètes
   */
  getStats() {
    return getModuleStats(this.project.data, this.tiroir, this.module, this.project.fiberType);
  }

  // ===== MODIFICATION DES DONNÉES =====

  /**
   * Changer le statut d'une fibre spécifique
   *
   * @param {number} fiberIndex - Index de la fibre (0-23)
   * @param {string} newStatus - Nouveau statut
   */
  changeFiberStatus(fiberIndex, newStatus) {
    const port = fiberIndex + 1;
    const oldStatus = this.getFiberStatus(fiberIndex);
    const normalizedStatus = normalizeStatus(newStatus);

    // Modifier dans project.data[]
    const success = setFiberStatus(this.project.data, this.tiroir, this.module, port, normalizedStatus);

    if (success) {
      // Tracker la dernière fibre modifiée
      this.lastModifiedFiber = port;

      // Sauvegarder et mettre à jour l'affichage
      this.saveModuleState();
      this.updateFiberDisplay(fiberIndex);
      this.updateStats();
    } else {
      console.error(`❌ Échec modification fibre ${port}`);
    }
  }

  /**
   * Passer au statut suivant pour une fibre (cycle N/A → Libre → Occupée)
   *
   * @param {number} fiberIndex - Index de la fibre (0-23)
   */
  cycleFiberStatus(fiberIndex) {
    // Bloquer si en mode vocal
    if (this.vocalMode) {
      return;
    }

    const currentStatus = this.getFiberStatus(fiberIndex);
    const currentIndex = STATUS_ORDER.indexOf(currentStatus);
    const newStatus = STATUS_ORDER[(currentIndex + 1) % STATUS_ORDER.length];

    this.changeFiberStatus(fiberIndex, newStatus);
  }

  /**
   * Appliquer un statut à toutes les fibres du module
   *
   * @param {string} status - Statut à appliquer
   */
  setAllFibers(status) {
    // Bloquer si en mode vocal
    if (this.vocalMode) {
      return;
    }

    const normalizedStatus = normalizeStatus(status);
    const newStatuses = new Array(this.project.fiberType).fill(normalizedStatus);

    // Modifier toutes les fibres en une fois
    const success = setModuleStatuses(this.project.data, this.tiroir, this.module, newStatuses);

    if (success) {
      this.lastModifiedFiber = this.project.fiberType;
      this.saveModuleState();
      this.renderFibers();
      this.updateStats();
    }
  }

  // ===== RECONNAISSANCE VOCALE =====

  /**
   * Démarrer le mode de reconnaissance vocale
   */
  startVocalMode() {
    // Validation et recherche de la prochaine fibre
    this.validateCurrentFiber();

    const nextFiber = this.findNextUnconfigueredFiber();
    if (!nextFiber) {
      this.showToast("Toutes les fibres sont déjà configurées", "info");
      return;
    }

    // Initialiser le mode vocal
    this.currentFiber = nextFiber;
    this.vocalMode = true;
    this.speechStatus = "Démarrage...";
    this.lastHeard = "";

    // Sauvegarder l'état et mettre à jour l'interface
    this.saveModuleState();
    this.updateVocalButtonAppearance();
    this.updateFiberHighlight();

    // Initialiser la reconnaissance vocale
    this.speechRecognition = new SpeechRecognition({
      onResult: (transcript, command) => this.handleVoiceCommand(transcript, command),
      onError: (error) => this.handleVoiceError(error),
      onStart: () => this.handleVoiceStart(),
      onEnd: () => this.handleVoiceEnd(),
    });

    this.speechRecognition.start();
  }

  /**
   * Arrêter le mode de reconnaissance vocale
   */
  stopVocalMode() {
    this.vocalMode = false;
    this.speechStatus = "Arrêté";

    if (this.speechRecognition) {
      this.speechRecognition.stop();
      this.speechRecognition = null;
    }

    // Sauvegarder l'arrêt et mettre à jour l'interface
    this.saveModuleState();
    this.updateVocalButtonAppearance();
    this.updateFiberHighlight();
  }

  /**
   * Gérer les commandes vocales reconnues
   *
   * @param {string} transcript - Texte reconnu
   * @param {string|null} command - Commande interprétée
   */
  handleVoiceCommand(transcript, command) {
    this.lastHeard = transcript;

    if (command && this.currentFiber <= this.project.fiberType) {
      // Commande reconnue et valide
      this.lastCommand = command;
      this.speechStatus = "En cours...";

      // Appliquer le changement
      this.changeFiberStatus(this.currentFiber - 1, command);

      // Logique de passage à la fibre suivante
      const nextFiber = this.findNextUnconfigueredFiber();

      if (nextFiber && nextFiber > this.currentFiber) {
        // Passer à la prochaine fibre non configurée
        this.currentFiber = nextFiber;
        this.updateFiberHighlight();
      } else if (!nextFiber) {
        // Toutes les fibres sont configurées
        this.stopVocalMode();
        this.speechStatus = "✅ Terminé - Toutes les fibres configurées";
        this.showToast("Module entièrement configuré !", "success");
      } else {
        // Continuer séquentiellement
        if (this.currentFiber < this.project.fiberType) {
          this.currentFiber++;
          this.updateFiberHighlight();
        } else {
          this.currentFiber = 1;
          this.stopVocalMode();
          this.speechStatus = "🔄 Fin du module - Retour au début";
        }
      }
    }

    this.updateVocalStatus();
  }

  /**
   * Gérer les erreurs de reconnaissance vocale
   *
   * @param {string} error - Type d'erreur
   */
  handleVoiceError(error) {
    console.error("❌ Erreur reconnaissance vocale:", error);

    if (error === "timeout") {
      this.stopVocalMode();
      this.speechStatus = "Arrêt - Timeout 30s";
      this.showToast("Reconnaissance vocale arrêtée (timeout)", "warning");
    } else {
      this.speechStatus = `Erreur: ${error}`;
    }

    this.updateVocalStatus();
  }

  /**
   * Gérer le démarrage de la reconnaissance
   */
  handleVoiceStart() {
    this.speechStatus = "En écoute...";
    this.updateVocalStatus();
  }

  /**
   * Gérer la fin d'un cycle de reconnaissance
   */
  handleVoiceEnd() {
    if (this.vocalMode) {
      // Redémarrer automatiquement si encore en mode vocal
      setTimeout(() => {
        if (this.vocalMode && this.speechRecognition) {
          this.speechRecognition.start();
        }
      }, 500);
    }
  }

  // ===== UTILITAIRES =====

  /**
   * Valider que currentFiber est dans les limites
   */
  validateCurrentFiber() {
    if (this.currentFiber < 1 || this.currentFiber > this.project.fiberType) {
      console.warn(`⚠️ currentFiber invalide: ${this.currentFiber}, reset à 1`);
      this.currentFiber = 1;
      this.saveModuleState();
      return false;
    }
    return true;
  }

  /**
   * Trouver la prochaine fibre non configurée
   *
   * @returns {number|null} Numéro de fibre (1-24) ou null si toutes configurées
   */
  findNextUnconfigueredFiber() {
    const statuses = this.getStatuses();

    // Chercher à partir de la position actuelle
    for (let i = this.currentFiber - 1; i < statuses.length; i++) {
      if (statuses[i] === "N/A") {
        return i + 1;
      }
    }

    // Si aucune trouvée, chercher depuis le début
    for (let i = 0; i < this.currentFiber - 1; i++) {
      if (statuses[i] === "N/A") {
        return i + 1;
      }
    }

    return null; // Toutes les fibres sont configurées
  }

  /**
   * Générer un ID de session unique
   *
   * @returns {string} ID de session
   */
  generateSessionId() {
    return `session_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
  }

  /**
   * Obtenir le statut suivant dans l'ordre cyclique
   *
   * @param {string} current - Statut actuel
   * @returns {string} Statut suivant
   */
  getNextStatus(current) {
    const idx = STATUS_ORDER.indexOf(current);
    return STATUS_ORDER[(idx + 1) % STATUS_ORDER.length];
  }

  /**
   * Obtenir un résumé de l'état du module
   *
   * @returns {Object} Snapshot de l'état
   */
  getStateSnapshot() {
    const stats = this.getStats();
    return {
      title: this.title,
      tiroir: this.tiroir,
      module: this.module,
      currentFiber: this.currentFiber,
      vocalMode: this.vocalMode,
      configured: stats.configured,
      total: stats.total,
      nextUnconfigured: this.findNextUnconfigueredFiber(),
      lastModified: this.lastModifiedFiber,
      sessionId: this.sessionId,
    };
  }

  // ===== NOTIFICATION ET ÉVÉNEMENTS =====

  /**
   * Notifier les autres composants des changements de données
   */
  notifyDataChanged() {
    // Déclencher un événement personnalisé pour mise à jour des stats
    const event = new CustomEvent(CUSTOM_EVENTS.DATA_CHANGED, {
      detail: {
        source: "module",
        moduleTitle: this.title,
        tiroir: this.tiroir,
        module: this.module,
        stats: this.getStats(),
      },
    });

    window.dispatchEvent(event);

    // Compatibilité avec l'ancien système
    if (window.tools && typeof window.tools.updateDisplayedStats === "function") {
      window.tools.updateDisplayedStats();
    }
  }

  /**
   * Afficher une notification toast
   *
   * @param {string} message - Message à afficher
   * @param {string} type - Type de toast (info, success, warning, error)
   */
  showToast(message, type = "info") {
    // Implémentation simple sans dépendance externe

    // Si une bibliothèque de toast est disponible, l'utiliser
    if (window.showToast) {
      window.showToast(message, type);
    }
  }

  // ===== INTERFACE UTILISATEUR =====

  /**
   * Mettre à jour la surbrillance de la fibre active
   */
  updateFiberHighlight() {
    // Retirer toutes les surbrillances
    this.container.querySelectorAll(".module-fiber-card").forEach((card) => {
      card.style.border = "";
      card.style.borderRadius = "";
      card.style.padding = "";
    });

    // Ajouter la surbrillance à la fibre courante si en mode vocal
    if (this.vocalMode && this.currentFiber <= this.project.fiberType) {
      const fiberCard = this.container.querySelector(`[data-fiber="${this.currentFiber}"]`);
      if (fiberCard) {
        fiberCard.style.border = "3px solid #ff4444";
        fiberCard.style.borderRadius = "8px";
        fiberCard.style.padding = "2px";
      }
    }
  }

  /**
   * Mettre à jour l'affichage d'une fibre spécifique
   *
   * @param {number} fiberIndex - Index de la fibre (0-23)
   */
  updateFiberDisplay(fiberIndex) {
    const fiberCard = this.container.querySelector(`[data-fiber="${fiberIndex + 1}"]`);
    if (!fiberCard) return;

    const status = this.getFiberStatus(fiberIndex);
    const statusElement = fiberCard.querySelector(".module-fiber-status");
    const imgElement = fiberCard.querySelector(".module-fiber-img");

    // Mettre à jour le statut affiché
    if (statusElement) {
      statusElement.textContent = status.trim(); // ← AJOUTER .trim()
      statusElement.style.backgroundColor = STATUS_COLORS[status];
    }

    // Mettre à jour l'image
    if (imgElement) {
      const imageName = STATUS_IMAGES[status] || STATUS_IMAGES["N/A"];
      imgElement.src = `${imageName}`;

      // Gestion d'erreur pour images manquantes
      imgElement.onerror = () => {
        imgElement.src = this.generateFallbackImage(status);
        console.warn(`⚠️ Image manquante pour statut ${status}, fallback utilisé`);
      };
    }
  }

  /**
   * Générer une image SVG de fallback
   *
   * @param {string} status - Statut de la fibre
   * @returns {string} Data URL de l'image SVG
   */
  generateFallbackImage(status) {
    const color = STATUS_COLORS[status] || "#f5f5f5";
    const text = status.substring(0, 2);

    return `data:image/svg+xml;base64,${btoa(`
      <svg width="40" height="120" viewBox="0 0 40 120" fill="none" xmlns="http://www.w3.org/2000/svg">
        <rect width="40" height="120" fill="${color}"/>
        <text x="20" y="60" text-anchor="middle" font-family="Arial" font-size="12" fill="white">${text}</text>
      </svg>
    `)}`;
  }

  /**
   * Mettre à jour les statistiques affichées
   */
  updateStats() {
    const stats = this.getStats();
    /*
    const configuredEl = this.container.querySelector(".module-stat-configured .module-stat-value");
    const libresEl = this.container.querySelector(".module-stat-libres .module-stat-value");
    const occupeesEl = this.container.querySelector(".module-stat-occupees .module-stat-value");
*/
    const configuredEl = this.container.querySelector(".module-stat-configured .ui-stat-value");
    const libresEl = this.container.querySelector(".module-stat-libres .ui-stat-value");
    const occupeesEl = this.container.querySelector(".module-stat-occupees .ui-stat-value");

    if (configuredEl) configuredEl.textContent = `${stats.configured} / ${stats.total}`;
    if (libresEl) libresEl.textContent = stats.libres;
    if (occupeesEl) occupeesEl.textContent = stats.occupees;
  }

  /**
   * Mettre à jour le statut vocal affiché
   */
  updateVocalStatus() {
    const statusEl = this.container.querySelector(".speech-main-status");
    const heardEl = this.container.querySelector(".speech-heard-text");
    const lastEl = this.container.querySelector(".speech-last-command");

    if (statusEl) {
      statusEl.innerHTML = `🔴 <strong>Fibre ${this.currentFiber.toString().padStart(2, "0")}/${
        this.project.fiberType
      }</strong> - ${this.speechStatus}`;
    }

    if (heardEl && this.lastHeard) {
      heardEl.innerHTML = `👂 Entendu: <strong>"${this.lastHeard}"</strong>`;
      heardEl.style.display = "block";
    } else if (heardEl) {
      heardEl.style.display = "none";
    }

    if (lastEl && this.lastCommand) {
      lastEl.innerHTML = `✅ Dernière: <strong>${this.lastCommand}</strong>`;
      lastEl.style.display = "block";
    } else if (lastEl) {
      lastEl.style.display = "none";
    }
  }

  /**
   * Mettre à jour l'apparence du bouton vocal
   */
  updateVocalButtonAppearance() {
    const vocalBtn = this.container.querySelector("#vocalToggle");
    if (!vocalBtn) return;

    if (this.vocalMode) {
      // Mode actif : bouton rouge avec stop
      vocalBtn.className = "module-vocal-toggle-btn vocal-active";
      vocalBtn.innerHTML = '<span class="vocal-icon">⏹</span>';
      vocalBtn.title = "Arrêter la reconnaissance vocale";
    } else {
      // Mode normal : bouton gris avec casque
      vocalBtn.className = "module-vocal-toggle-btn";
      vocalBtn.innerHTML = '<span class="vocal-icon">🎧</span>';
      vocalBtn.title = "Démarrer la reconnaissance vocale";
    }
  }

  // ===== CONFIGURATION DES ÉVÉNEMENTS =====

  /**
   * Configurer le bouton retour
   */
  setupBackButton() {
    const backBtn = this.container.querySelector("#backToTiroirs");
    if (backBtn) {
      backBtn.addEventListener("click", () => {
        // Fermer le ruban de configuration
        window.dispatchEvent(
          new CustomEvent(CUSTOM_EVENTS.CLOSE_RIBAN_REQUESTED, {
            detail: { source: "backButton" },
          })
        );
      });
    }
  }

  /**
   * Configurer le bouton vocal
   */
  setupVocalButton() {
    const vocalBtn = this.container.querySelector("#vocalToggle");
    if (!vocalBtn) return;

    // Mettre à jour l'apparence selon l'état
    this.updateVocalButtonAppearance();

    // Événement de clic
    vocalBtn.addEventListener("click", () => {
      if (!this.vocalMode) {
        this.startVocalMode();
      } else {
        this.stopVocalMode();
      }
    });
  }

  // ===== RENDU =====

  /**
   * Rendu des fibres du module
   */
  renderFibers() {
    const fiberGrid = this.container.querySelector(".module-fiber-grid");
    if (!fiberGrid) return;

    fiberGrid.innerHTML = "";
    const statuses = this.getStatuses();

    for (let i = 0; i < this.project.fiberType; i++) {
      const fiberNum = (i + 1).toString().padStart(2, "0");
      const status = statuses[i];

      const bgColor = STATUS_COLORS[status];

      const fiberCard = createElement("div", {
        className: "module-fiber-card",
        "data-fiber": i + 1,
      });
      fiberCard.innerHTML = `
        <div class="module-fiber-num">${fiberNum}</div>
        <div class="module-fiber-img-wrapper">
        <img class="module-fiber-img" src="${STATUS_IMAGES[status] || STATUS_IMAGES["N/A"]}" alt="${status}">        
        </div>
        <div class="module-fiber-status" style="background-color: ${bgColor};">
        ${status.trim()}
        </div>
        <div class="module-fiber-btn-wrapper">
          <button class="module-fiber-btn" ${this.vocalMode ? "disabled" : ""}>🔁</button>
        </div>
      `;

      // Gestion d'erreur pour les images
      const imgElement = fiberCard.querySelector(".module-fiber-img");
      imgElement.onerror = () => {
        imgElement.src = this.generateFallbackImage(status);
      };

      // Ajouter l'événement de clic sur le bouton
      const btn = fiberCard.querySelector(".module-fiber-btn");
      if (!this.vocalMode) {
        btn.addEventListener("click", () => {
          this.cycleFiberStatus(i);
        });
      }

      fiberGrid.appendChild(fiberCard);
    }
  }

  /**
   * Rendu complet du module
   */
  render() {
    const stats = this.getStats();

    // Structure HTML complète
    this.container.innerHTML = `
      <div class="module-container">
        <div class="module-header">
          <h3 class="module-title">${this.title}</h3>
          <button class="module-vocal-toggle-btn" id="vocalToggle">
            <span class="vocal-icon">🎧</span>
          </button>
        </div>
        
        <!-- Statistiques -->
        <div class="module-stats-row">
          <div class="ui-stat-box ui-stat-gray module-stat-configured">
            <div class="ui-stat-label">Fibres Configurées</div>
            <div class="ui-stat-value">${stats.configured} / ${stats.total}</div>
          </div>
          <div class="ui-stat-box ui-stat-green module-stat-libres">
            <div class="ui-stat-label">Fibres Libres</div>
            <div class="ui-stat-value">${stats.libres}</div>
          </div>
          <div class="ui-stat-box ui-stat-yellow module-stat-occupees">
            <div class="ui-stat-label">Fibres Occupées</div>
            <div class="ui-stat-value">${stats.occupees}</div>
          </div>
        </div>
        
        <!-- Grille des fibres -->
        <div class="module-fiber-grid"></div>
        
        <!-- Actions rapides avec bouton retour -->
        <div class="module-actions">
          <button class="ui-btn ui-btn-gray" id="allNA" ${this.vocalMode ? "disabled" : ""}>
            ♻️ Tout N/A
          </button>
          <button class="ui-btn ui-btn-success" id="allLibre" ${this.vocalMode ? "disabled" : ""}>
            🟢 Tout Libre
          </button>
          <button class="ui-btn ui-btn-warning" id="allOccupee" ${this.vocalMode ? "disabled" : ""}>
            🟡 Tout Occupée
          </button>
          <button class="ui-btn ui-btn-primary module-btn-back" id="backToTiroirs">
            ← Retour Tiroirs
          </button>
        </div>
        
        <!-- Zone de statut vocal (masquée par défaut) -->
        <div class="module-vocal-status" style="display: none;">
          <div class="speech-main-status"></div>
          <div class="speech-heard-text"></div>
          <div class="speech-last-command"></div>
        </div>
      </div>
    `;

    // Rendu des sous-composants
    this.renderFibers();

    // Configuration des événements
    this.setupVocalButton();
    this.setupBackButton();

    // Actions rapides
    this.container.querySelector("#allNA").addEventListener("click", () => {
      if (!this.vocalMode) this.setAllFibers("N/A");
    });

    this.container.querySelector("#allLibre").addEventListener("click", () => {
      if (!this.vocalMode) this.setAllFibers("Libre");
    });

    this.container.querySelector("#allOccupee").addEventListener("click", () => {
      if (!this.vocalMode) this.setAllFibers("Occupée");
    });
  }

  // ===== NETTOYAGE =====

  /**
   * Nettoyer les ressources du composant
   */
  destroy() {
    // Arrêter la reconnaissance vocale
    if (this.speechRecognition) {
      this.speechRecognition.stop();
      this.speechRecognition = null;
    }

    // Nettoyer les événements
    const buttons = this.container?.querySelectorAll("button");
    buttons?.forEach((btn) => {
      btn.removeEventListener("click", btn.onclick);
    });

    // Nettoyer les références
    this.container = null;
    this.project = null;
  }
}
